import { defineComponent, inject, ref, computed, watch, toRefs, onMounted, onBeforeUnmount, onUnmounted } from '@vue/composition-api';
import { AsyncGogoRouteView, loadGogoLib } from './runtime-loader';
import RuntimeReport from '@/reporter/RuntimeReport';
import { reportTelemetry } from '@components/telemetry';
import { useStore } from '@/store';
import { TYPE, PAGE_TYPE } from '@/loaders/page/consts';
import { useGogoCssReset } from './css-reset-counter';
import { getAppParams } from '@/utils/getAppParams';
import { useGogoWatermark } from '@components/watermark/useGogoWatermark';
export default defineComponent({
    name: 'GogoRuntimeRouterView',
    components: {
        AsyncGogoRouteView,
    },
    props: {
        projectId: {
            type: String,
            required: true,
        },
        pageId: {
            type: String,
            required: true,
        },
        forcePath: {
            type: String,
            default: '',
        },
        forceProps: {
            type: Object,
            default: null,
        },
    },
    setup(props) {
        const { pageId } = toRefs(props);
        const gogoRef = ref(null);
        const reporter = ref(null);
        const store = useStore();
        const gogoHost = inject('gogoHostRef');
        const gogoRuntimeViewInstances = inject('gogoRuntimeViewInstances');
        loadGogoLib('runtime'); // 触发加载 lib - 虽然好像没啥必要但是也搞一下吧
        useGogoCssReset();
        const self = { pageId };
        // gogoRuntimeViewInstances 仅在 GogoRuntimeRouterWrapper / GogoReactRuntimeWrapper 上下文中存在；
        // 直接在 pagelet 等场景下使用时 inject 返回 undefined，需防御
        const isOrphan = !gogoRuntimeViewInstances; // 比如低代码配置页面那边
        if (gogoRuntimeViewInstances) {
            gogoRuntimeViewInstances.value = [...gogoRuntimeViewInstances.value, self];
            onUnmounted(() => {
                gogoRuntimeViewInstances.value = gogoRuntimeViewInstances.value.filter(item => item !== self);
            });
        }
        const project = computed(() => store.state.runtime.project);
        const page = ref({});
        const getPageDetail = async (pageId) => await store.dispatch('runtime/getPageDetail', {
            projectId: props.projectId,
            pageId,
            env: getAppParams('env'),
            branch: getAppParams('branch'),
        });
        const { rendererGogoWatermark } = useGogoWatermark();
        onMounted(() => {
            // 水印
            watch(() => pageId.value, async (newPageId, oldPageId) => {
                if (!newPageId || newPageId === oldPageId)
                    return;
                try {
                    page.value = await getPageDetail(newPageId);
                }
                catch {
                    return;
                }
                reportTelemetry('pageView', newPageId, { pageTitle: page.value?.title || '' });
                rendererGogoWatermark({
                    id: 'wuji-pagelet-watermark',
                    container: gogoRef.value,
                    project: project.value,
                    page: page.value,
                });
            }, { immediate: true });
            // 上报
            watch(() => pageId.value, (newPageId, oldPageId) => {
                // 上报pv和pgOut
                const { projectId, env } = window.RUNTIME_CONFIGS;
                // 销毁旧的 reporter
                if (oldPageId && reporter.value) {
                    reporter.value?.pgOut()?.destroy();
                    reporter.value = null;
                }
                // 创建新的 reporter
                reporter.value = new RuntimeReport({
                    isAIPage: true,
                    projectInfo: {
                        projectId,
                        projectName: project.value?.name,
                        parentId: project.value?.parentId,
                    },
                    pageId: newPageId,
                    envId: env,
                    pageInfo: {
                        type: TYPE.AI,
                        pageType: PAGE_TYPE.CONTENT,
                        contentType: 'normal',
                    },
                    $dom: gogoRef.value,
                    agentReport: project.value?.advanceConfig?.agentReport ?? false,
                });
                reporter.value.pv();
            }, { immediate: true });
        });
        onBeforeUnmount(() => {
            reporter.value?.pgOut()?.destroy();
            reporter.value = null;
        });
        return {
            gogoRef,
            gogoHost,
            project,
            isOrphan,
        };
    },
});
